import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight, CreditCard, LayoutGrid, ShieldCheck, Zap } from "lucide-react";
import billingService from "../../services/billingService";
import SEO from "../../components/SEO/SEO";
import LandingHero from "../../components/landing/LandingHero";
import BenefitGrid from "../../components/landing/BenefitGrid";
import BookingSteps from "../../components/landing/BookingSteps";
import TeamsSection from "../../components/landing/TeamsSection";
import { FinalCtaCard, PartnerSection } from "../../components/landing/PartnerAndCta";
import StaySearchBar, { StaySubcategory } from "./components/StaySearchBar";
import StayIntro from "./components/StayIntro";
import StayTypes from "./components/StayTypes";
import StayListings from "./components/StayListings";
import StayYourWay from "./components/StayYourWay";
import StayConnected from "./components/StayConnected";
import logger from "../../utils/logger";
import "./index.css";

const BENEFITS = [
  {
    icon: ShieldCheck,
    title: "Verified Accommodation",
    description: "Verified listings, reviews and ratings help you book with confidence.",
  },
  { icon: Zap, title: "Simple Booking", description: "Discover, compare and book accommodation from one platform." },
  { icon: CreditCard, title: "Secure Payments", description: "Pay securely online." },
  { icon: LayoutGrid, title: "Flexible Options", description: "Short stays, extended stays, business or leisure." },
  { icon: ArrowRight, title: "One Platform", description: "Connect your stay with Work and Play across SPACIA." },
];

const STEPS = [
  { title: "Discover", description: "Search and compare accommodation near you or in your destination city." },
  { title: "Choose", description: "Review accommodation, availability and verified information." },
  { title: "Book", description: "Confirm your booking with secure online payment." },
  { title: "Stay", description: "Arrive and enjoy your accommodation." },
  { title: "Review", description: "Rate your experience and help others discover great stays." },
];

export default function StayLandingScreen() {
  const history = useHistory();
  const [subcategories, setSubcategories] = useState<StaySubcategory[]>([]);

  // Stay category is ID 2 in the public registry
  useEffect(() => {
    billingService
      .getPublicServiceSubcategories(2)
      .then((res: any) => {
        const subs: any[] = Array.isArray(res.data)
          ? res.data
          : res.data?.data || res.data?.content || res.data?.items || [];
        if (subs.length > 0) setSubcategories(subs);
      })
      .catch((err: any) => logger.warn("StayLanding: failed loading subcategories for category 2:", err));
  }, []);

  return (
    <div className="stay-landing-container min-h-screen flex flex-col">
      <SEO
        title="Stay Somewhere That Fits Your Journey - SPACIA"
        description="Wherever you're headed, find a place to stay that fits how and why you're travelling."
      />

      <LandingHero
        image="/figma-assets/stay/hero.jpg"
        imageAlt="Premium hotel room"
        badge="SPACIA STAY"
        titleLines={["Find a Place", "That Feels Right"]}
        titleSize="xl"
        lead="Wherever the journey takes you, find a place to stay that fits how and why you're travelling."
        body="From hotels and serviced apartments to boutique properties, SPACIA makes it simple to discover, compare and book accommodation for business or leisure."
        primaryCta={{
          label: "Explore Stays",
          onClick: () => history.push("/workspaces-search?type=stay&categoryId=2"),
        }}
        secondaryCta={{ label: "Get Started", onClick: () => history.push("/package-signup") }}
        desktopHeight={903}
        shade={{ bottom: 0.85, side: 0.4, lead: 0.7 }}
        search={<StaySearchBar subcategories={subcategories} />}
      />
      <StayIntro subcategories={subcategories} />
      <StayTypes subcategories={subcategories} />
      <StayListings subcategories={subcategories} />
      <StayYourWay />
      <BenefitGrid
        eyebrow="Benefits"
        title="A Better Way to Book Your Stay"
        items={BENEFITS}
        variant="white-cards"
        headingSize="sm"
      />
      <StayConnected />
      <BookingSteps eyebrow="Simple Process" title="How Booking a Stay Works" steps={STEPS} />
      <TeamsSection
        title="Accommodation for Your Team"
        description="Give your people flexible access to workspaces and travel accommodation across every location you operate in."
        uses={["Business travel", "Team trips", "Offsites", "Extended assignments", "Relocations", "Client hosting"]}
        image="/figma-assets/stay/teams.jpg"
        imageAlt="Team accommodation"
        imageGradient
      />
      <PartnerSection
        eyebrow="For Partners"
        title="List Your Property on SPACIA"
        description="List your space or venue and reach a growing community of users looking for verified accommodation for business and leisure."
        tags={["Hotels", "Serviced apartments", "Boutique properties"]}
        image="/figma-assets/stay/list-property.jpg"
        imageAlt="List your property"
      />
      <FinalCtaCard
        eyebrow="Book Your Stay"
        title="Find a Place That Feels Right"
        description="Wherever your journey takes you, SPACIA makes it simple to discover, compare and book accommodation for business or leisure."
        primaryCta={{
          label: "Explore Stays",
          onClick: () => history.push("/workspaces-search?type=stay&categoryId=2"),
        }}
        secondaryCta={{ label: "Get Started", onClick: () => history.push("/package-signup") }}
      />
    </div>
  );
}
